import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
  Building2, Users, Search, Plus, Power, PowerOff, Eye, UserCog,
  Shield, ChevronLeft, BarChart3, Package, ArrowRightLeft,
  Palette, Globe, Trash2, Pencil, Save, Briefcase, User
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { HelpTip } from "../components/HelpTip";

const SuperAdminPanel = () => {
  const { user, token } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [impersonationLogs, setImpersonationLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showModuleConfig, setShowModuleConfig] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "empleado" });
  const [redirects, setRedirects] = useState({ calendly_redirect_url: "" });
  const [sendgridConfig, setSendgridConfig] = useState({ sandbox: true, metadata: { from_email: "" }, credentials: { api_key: "" } });
  const [calendlyConfig, setCalendlyConfig] = useState({ sandbox: true, redirect_url: "", credentials: { client_id: "", client_secret: "" } });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const opts = { headers, withCredentials: true };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, companiesRes, modulesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, opts),
        axios.get(`${API}/companies`, opts),
        axios.get(`${API}/modules`, opts)
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setModules(modulesRes.data);
      const [redirectsRes, sendgridRes, calendlyRes] = await Promise.all([
        axios.get(`${API}/superadmin/integrations/redirects`, opts).catch(() => ({ data: { calendly_redirect_url: "" } })),
        axios.get(`${API}/superadmin/integrations/sendgrid`, opts).catch(() => ({ data: {} })),
        axios.get(`${API}/superadmin/integrations/calendly`, opts).catch(() => ({ data: {} }))
      ]);
      setRedirects(redirectsRes.data || { calendly_redirect_url: "" });
      setSendgridConfig(previous => ({ ...previous, ...(sendgridRes.data || {}) }));
      setCalendlyConfig(previous => ({ ...previous, ...(calendlyRes.data || {}) }));
    } catch { toast.error("Error al cargar datos"); }
    finally { setLoading(false); }
  }, [token]);

  const handleSaveSuperIntegration = async (integration, payload) => {
    try {
      await axios.post(`${API}/superadmin/integrations/${integration}`, payload, opts);
      toast.success(`Integración ${integration} guardada`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `No se pudo guardar ${integration}`);
    }
  };

  useEffect(() => { if (user?.role === "super_admin") fetchData(); }, [user, fetchData]);

  const fetchCompanyDetail = async (companyId) => {
    try {
      const res = await axios.get(`${API}/companies/${companyId}`, opts);
      setSelectedCompany(res.data);
      setCompanyUsers(res.data.users || []);
      setTab("companies");
    } catch { toast.error("Error al cargar empresa"); }
  };

  const fetchImpersonationLogs = async () => {
    try {
      const res = await axios.get(`${API}/impersonation-logs`, opts);
      setImpersonationLogs(res.data);
    } catch { toast.error("Error al cargar logs"); }
  };

  const handleToggleCompany = async (companyId) => {
    try {
      const res = await axios.put(`${API}/companies/${companyId}/toggle-status`, {}, opts);
      toast.success(res.data.message);
      fetchData();
      if (selectedCompany?.company_id === companyId) fetchCompanyDetail(companyId);
    } catch { toast.error("Error al cambiar estado"); }
  };

  const handleCreateUser = async () => {
    if (!selectedCompany) return;
    try {
      await axios.post(`${API}/companies/${selectedCompany.company_id}/users`, newUser, opts);
      toast.success("Usuario creado");
      setShowCreateUser(false);
      setNewUser({ name: "", email: "", password: "", role: "empleado" });
      fetchCompanyDetail(selectedCompany.company_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear usuario"); }
  };

  const handleToggleUser = async (userId) => {
    if (!selectedCompany) return;
    try {
      const res = await axios.put(`${API}/companies/${selectedCompany.company_id}/users/${userId}/toggle-status`, {}, opts);
      toast.success(res.data.message);
      fetchCompanyDetail(selectedCompany.company_id);
    } catch { toast.error("Error al cambiar estado del usuario"); }
  };

  const handleImpersonate = async (userId) => {
    try {
      const res = await axios.post(`${API}/impersonate/${userId}`, {}, opts);
      localStorage.setItem("original_admin_token", token);
      localStorage.setItem("original_admin_user", JSON.stringify(user));
      localStorage.setItem("impersonating", "true");
      localStorage.setItem("token", res.data.token);
      window.location.href = "/dashboard";
    } catch (e) { toast.error(e.response?.data?.detail || "Error al suplantar"); }
  };

  const handleUpdateModules = async (selectedModules) => {
    if (!selectedCompany) return;
    try {
      await axios.put(`${API}/companies/${selectedCompany.company_id}`, { modules: selectedModules }, opts);
      toast.success("Módulos actualizados");
      setShowModuleConfig(false);
      fetchCompanyDetail(selectedCompany.company_id);
    } catch { toast.error("Error al actualizar módulos"); }
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await axios.delete(`${API}/companies/${companyId}`, opts);
      toast.success("Empresa eliminada");
      setShowDeleteConfirm(null);
      setSelectedCompany(null);
      fetchData();
    } catch { toast.error("Error al eliminar empresa"); }
  };

  const handleSaveCompany = async (data) => {
    if (!selectedCompany) return;
    try {
      await axios.put(`${API}/companies/${selectedCompany.company_id}`, data, opts);
      toast.success("Empresa actualizada");
      setShowEditCompany(false);
      fetchCompanyDetail(selectedCompany.company_id);
      fetchData();
    } catch { toast.error("Error al actualizar empresa"); }
  };

  const filteredCompanies = companies.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (user?.role !== "super_admin") return <Layout><div className="p-8"><p>Acceso denegado</p></div></Layout>;

  return (
    <Layout>
      <div className="p-4 md:p-8 space-y-6" data-testid="super-admin-panel">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Panel Super Admin</h1>
            <p className="text-slate-500">Backoffice de gestión de empresas</p>
          </div>
          <Badge className="bg-red-100 text-red-700"><Shield className="w-3 h-3 mr-1" />Super Admin</Badge>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard"><BarChart3 className="w-4 h-4 mr-1" />Dashboard</TabsTrigger>
            <TabsTrigger value="companies" data-testid="tab-companies"><Building2 className="w-4 h-4 mr-1" />Empresas</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations"><Globe className="w-4 h-4 mr-1" />Integraciones</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs" onClick={fetchImpersonationLogs}><ArrowRightLeft className="w-4 h-4 mr-1" />Suplantaciones</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard label="Total Empresas" value={stats.total_companies} icon={Building2} color="bg-blue-500" />
                <StatCard label="Activas" value={stats.active_companies} icon={Power} color="bg-emerald-500" />
                <StatCard label="Inactivas" value={stats.inactive_companies} icon={PowerOff} color="bg-red-500" />
                <StatCard label="Usuarios" value={stats.total_users} icon={Users} color="bg-violet-500" />
                <StatCard label="Empleados" value={stats.total_employees} icon={UserCog} color="bg-amber-500" />
              </div>
            )}
            <Card className="mt-6">
              <CardHeader><CardTitle>Empresas Recientes</CardTitle></CardHeader>
              <CardContent>
                <CompanyTable companies={companies.slice(0, 10)} onView={(c) => fetchCompanyDetail(c.company_id)} onToggle={handleToggleCompany} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="companies">
            {selectedCompany ? (
              <CompanyDetail
                company={selectedCompany} users={companyUsers} modules={modules}
                onBack={() => setSelectedCompany(null)}
                onToggleCompany={handleToggleCompany}
                onToggleUser={handleToggleUser}
                onImpersonate={handleImpersonate}
                onCreateUser={() => setShowCreateUser(true)}
                onConfigModules={() => setShowModuleConfig(true)}
                onEditCompany={() => setShowEditCompany(true)}
                onDeleteCompany={() => setShowDeleteConfirm(selectedCompany.company_id)}
              />
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Buscar empresa..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="search-companies" />
                  </div>
                  <Button onClick={() => setShowCreateCompany(true)} data-testid="create-company-btn"><Plus className="w-4 h-4 mr-2" />Crear Empresa</Button>
                </div>
                <CompanyTable companies={filteredCompanies} onView={(c) => fetchCompanyDetail(c.company_id)} onToggle={handleToggleCompany} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="integrations">
            <div className="grid xl:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Calendly OAuth</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-sm"><strong>Redirect URL HumanOZ:</strong><br />{redirects.calendly_redirect_url || calendlyConfig.redirect_url || "Cargando..."}</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Client ID</Label><Input value={calendlyConfig.credentials?.client_id || ""} onChange={(e) => setCalendlyConfig(p => ({ ...p, credentials: { ...p.credentials, client_id: e.target.value } }))} data-testid="calendly-client-id-input" /></div>
                    <div><Label>Client Secret</Label><Input type="password" value={calendlyConfig.credentials?.client_secret || ""} onChange={(e) => setCalendlyConfig(p => ({ ...p, credentials: { ...p.credentials, client_secret: e.target.value } }))} data-testid="calendly-client-secret-input" /></div>
                  </div>
                  <Button onClick={() => handleSaveSuperIntegration('calendly', { ...calendlyConfig, redirect_url: redirects.calendly_redirect_url || calendlyConfig.redirect_url })} data-testid="save-calendly-config-btn"><Save className="w-4 h-4 mr-2" />Guardar Calendly</Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>SendGrid</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>From Email</Label><Input value={sendgridConfig.metadata?.from_email || ""} onChange={(e) => setSendgridConfig(p => ({ ...p, metadata: { ...p.metadata, from_email: e.target.value } }))} data-testid="sendgrid-from-email-input" /></div>
                  <div><Label>API Key</Label><Input type="password" value={sendgridConfig.credentials?.api_key || ""} onChange={(e) => setSendgridConfig(p => ({ ...p, credentials: { ...p.credentials, api_key: e.target.value } }))} data-testid="sendgrid-api-key-input" /></div>
                  <Button onClick={() => handleSaveSuperIntegration('sendgrid', sendgridConfig)} data-testid="save-sendgrid-config-btn"><Save className="w-4 h-4 mr-2" />Guardar SendGrid</Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="logs">
            <Card>
              <CardHeader><CardTitle>Historial de Suplantaciones</CardTitle></CardHeader>
              <CardContent>
                {impersonationLogs.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No hay registros de suplantación</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Admin</TableHead><TableHead>Usuario Suplantado</TableHead><TableHead>Fecha</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {impersonationLogs.map((log, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{log.admin_email}</TableCell>
                          <TableCell>{log.target_email}</TableCell>
                          <TableCell>{new Date(log.created_at).toLocaleString("es-MX")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <CreateCompanyDialog open={showCreateCompany} onOpenChange={setShowCreateCompany} opts={opts} onSuccess={() => { setShowCreateCompany(false); fetchData(); }} />

        <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Usuario para {selectedCompany?.name}</DialogTitle>
              <DialogDescription>Asigna un usuario a esta empresa</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre *</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="user-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="user-email-input" /></div>
              <div><Label>Contraseña *</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="user-password-input" /></div>
              <div>
                <Label>Rol</Label>
                <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                  <option value="admin_empresa">Admin Empresa</option>
                  <option value="gerente_rh">Gerente RH</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="empleado">Empleado</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateUser(false)}>Cancelar</Button>
              <Button onClick={handleCreateUser} disabled={!newUser.name || !newUser.email || !newUser.password} data-testid="submit-create-user">Crear Usuario</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ModuleConfigDialog open={showModuleConfig} onOpenChange={setShowModuleConfig} modules={modules} companyModules={selectedCompany?.modules || []} onSave={handleUpdateModules} />

        {showEditCompany && selectedCompany && (
          <EditCompanyDialog open={showEditCompany} onOpenChange={setShowEditCompany} company={selectedCompany} onSave={handleSaveCompany} />
        )}

        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Eliminar Empresa</DialogTitle><DialogDescription>Esta acción eliminará la empresa, todos sus usuarios y empleados. No se puede deshacer.</DialogDescription></DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={() => handleDeleteCompany(showDeleteConfirm)} data-testid="confirm-delete-btn">Eliminar Empresa</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// ============ CREATE COMPANY DIALOG ============
const INDUSTRIES = [
  "Agricultura, ganadería y pesca", "Minería y extracción", "Industria alimentaria",
  "Industria de bebidas y tabaco", "Fabricación de textiles y prendas de vestir",
  "Industria del cuero y calzado", "Industria de la madera", "Industria del papel",
  "Impresión e industrias conexas", "Fabricación de productos químicos",
  "Industria del plástico y hule", "Fabricación de productos minerales no metálicos",
  "Industrias metálicas básicas", "Fabricación de productos metálicos",
  "Fabricación de maquinaria y equipo", "Fabricación de equipo electrónico",
  "Fabricación de equipo de transporte", "Industria automotriz y autopartes",
  "Industria aeroespacial", "Fabricación de muebles y productos relacionados",
  "Construcción de edificaciones", "Construcción de obra civil",
  "Comercio al por mayor", "Comercio al por menor", "Tiendas de autoservicio y departamentales",
  "Comercio electrónico y marketplace",
  "Transporte aéreo", "Transporte terrestre de carga", "Transporte terrestre de pasajeros",
  "Mensajería y paquetería", "Logística y almacenamiento",
  "Telecomunicaciones", "Desarrollo de software y TI", "Consultoría en tecnologías de información",
  "Servicios de datos y hosting", "Ciberseguridad", "Inteligencia artificial y datos",
  "Fintech y servicios financieros digitales",
  "Banca y servicios financieros", "Seguros y fianzas", "Bienes raíces y desarrollo inmobiliario",
  "Servicios legales y notariales", "Servicios contables y fiscales",
  "Consultoría empresarial y de gestión", "Investigación y desarrollo científico",
  "Publicidad y relaciones públicas", "Diseño gráfico e industrial",
  "Agencias de marketing digital", "Agencias de empleo y reclutamiento",
  "Servicios de limpieza y mantenimiento", "Servicios de seguridad privada",
  "Educación básica y media", "Educación superior y universitaria",
  "Capacitación y formación empresarial",
  "Servicios de salud y hospitales", "Clínicas y consultorios médicos",
  "Laboratorios clínicos y de diagnóstico", "Farmacéutica",
  "Dispositivos médicos", "Salud mental y bienestar",
  "Restaurantes y servicios de alimentos", "Hoteles y alojamiento",
  "Turismo y agencias de viajes", "Entretenimiento y espectáculos",
  "Centros deportivos y fitness", "Spa y salones de belleza",
  "Asociaciones civiles y ONG", "Gobierno federal", "Gobierno estatal", "Gobierno municipal",
  "Energía eléctrica", "Petróleo y gas", "Energías renovables",
  "Producción cinematográfica y audiovisual", "Desarrollo de videojuegos",
  "Cooperativas y economía social", "Franquicias",
  "Startups y emprendimiento", "Veterinaria y cuidado animal",
  "Joyería y relojería", "Artesanías y productos artesanales", "Otro"
];

const CreateCompanyDialog = ({ open, onOpenChange, opts, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [planType, setPlanType] = useState("regular");
  const [form, setForm] = useState({
    name: "", rfc: "", email: "", phone: "", address: "",
    industry: "", contact_name: "", contact_email: "", contact_phone: "", contact_position: "",
    plan: "estandar", max_employees: 10,
    admin_name: "", admin_email: "", admin_password: "",
    wl_app_name: "", wl_domain: "", wl_primary_color: "#10b981", wl_secondary_color: "#059669", wl_logo_url: ""
  });
  const [saving, setSaving] = useState(false);
  const [industrySearch, setIndustrySearch] = useState("");

  const resetForm = () => {
    setStep(1); setPlanType("regular"); setIndustrySearch("");
    setForm({ name: "", rfc: "", email: "", phone: "", address: "", industry: "", contact_name: "", contact_email: "", contact_phone: "", contact_position: "", plan: "estandar", max_employees: 10, admin_name: "", admin_email: "", admin_password: "", wl_app_name: "", wl_domain: "", wl_primary_color: "#10b981", wl_secondary_color: "#059669", wl_logo_url: "" });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (planType === "whitelabel") payload.plan = "white_label";
      await axios.post(`${API}/companies`, payload, opts);
      toast.success("Empresa creada exitosamente");
      resetForm(); onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear empresa"); }
    finally { setSaving(false); }
  };

  const canSubmit = form.name && form.admin_name && form.admin_email && form.admin_password;
  const filteredIndustries = INDUSTRIES.filter(i => i.toLowerCase().includes(industrySearch.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{step === 1 ? "Tipo de Empresa" : step === 2 ? "Datos de la Empresa" : step === 3 ? "Contacto Principal" : "Administrador"}</DialogTitle>
          <DialogDescription>Paso {step} de 4</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-2 gap-4">
            <button onClick={() => { setPlanType("regular"); setStep(2); }} className="p-6 rounded-xl border-2 text-left transition-all hover:border-emerald-400 hover:bg-emerald-50 border-slate-200" data-testid="plan-type-regular">
              <Building2 className="w-8 h-8 text-emerald-600 mb-3" />
              <h3 className="font-bold text-lg">Empresa Regular</h3>
              <p className="text-sm text-slate-500 mt-1">Planes por empleado/mes con marca Human-OZ</p>
              <div className="flex gap-1 mt-3 flex-wrap">
                <Badge className="bg-slate-100 text-slate-700 text-xs">Estándar $89/emp</Badge>
                <Badge className="bg-blue-100 text-blue-700 text-xs">Avanzado $129/emp</Badge>
                <Badge className="bg-violet-100 text-violet-700 text-xs">Pro $159/emp</Badge>
              </div>
            </button>
            <button onClick={() => { setPlanType("whitelabel"); setForm(f => ({ ...f, plan: "white_label", max_employees: 20 })); setStep(2); }} className="p-6 rounded-xl border-2 text-left transition-all hover:border-violet-400 hover:bg-violet-50 border-slate-200" data-testid="plan-type-wl">
              <Palette className="w-8 h-8 text-violet-600 mb-3" />
              <h3 className="font-bold text-lg">White Label</h3>
              <p className="text-sm text-slate-500 mt-1">Marca propia, dominio personalizado, todos los módulos</p>
              <div className="flex gap-1 mt-3 flex-wrap">
                <Badge className="bg-violet-100 text-violet-700 text-xs">$30K/20emp</Badge>
                <Badge className="bg-violet-100 text-violet-700 text-xs">$60K/50emp</Badge>
                <Badge className="bg-violet-100 text-violet-700 text-xs">$100K/50+</Badge>
              </div>
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Razón Social *<HelpTip title="Razón Social"><p>Nombre legal de la empresa tal como aparece en el SAT.</p><p className="mt-1 italic">Ej: "Comercializadora MexTrade S.A. de C.V."</p></HelpTip></Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" placeholder="Empresa S.A. de C.V." /></div>
              <div><Label>RFC<HelpTip title="RFC"><p>Registro Federal de Contribuyentes. Se usa para facturación y cumplimiento SAT.</p><p className="mt-1 italic">Ej: "MXT200101ABC"</p></HelpTip></Label><Input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value })} placeholder="ABC123456789" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email Corporativo</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@empresa.com" /></div>
              <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="55 1234 5678" /></div>
            </div>
            <div><Label>Dirección<HelpTip title="Dirección"><p>Domicilio fiscal de la empresa para documentos y facturación.</p></HelpTip></Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Av. Reforma 123, Col. Centro, CDMX" /></div>
            <div>
              <Label>Giro / Industria *<HelpTip title="Giro de la Empresa"><p>Selecciona la industria principal. Esto permite personalizar reportes y métricas del sector.</p><p className="mt-1 italic">Ej: Una agencia digital seleccionaría "Agencias de marketing digital"</p></HelpTip></Label>
              <Input placeholder="Buscar industria..." value={industrySearch} onChange={(e) => setIndustrySearch(e.target.value)} className="mb-1" />
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="industry-select">
                <option value="">Seleccionar industria...</option>
                {filteredIndustries.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>

            {planType === "regular" && (
              <div className="grid grid-cols-2 gap-3 border-t pt-4">
                <div>
                  <Label>Plan<HelpTip title="Planes"><p><strong>Estándar $89/emp:</strong> Gestión básica de personal</p><p><strong>Avanzado $129/emp:</strong> + Nómina + Asistencia</p><p><strong>Pro $159/emp:</strong> Todos los módulos + IA</p></HelpTip></Label>
                  <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                    <option value="estandar">Estándar - $89/empleado/mes</option>
                    <option value="avanzado">Avanzado - $129/empleado/mes</option>
                    <option value="pro">Pro - $159/empleado/mes</option>
                  </select>
                </div>
                <div><Label>Empleados Estimados</Label><Input type="number" value={form.max_employees} onChange={(e) => setForm({ ...form, max_employees: parseInt(e.target.value) || 10 })} /></div>
              </div>
            )}

            {planType === "whitelabel" && (
              <div className="border-t pt-4 space-y-3">
                <h3 className="font-semibold text-sm text-violet-700 flex items-center gap-2"><Palette className="w-4 h-4" />White Label<HelpTip title="White Label"><p>Tu cliente verá el sistema con su propia marca: logo, nombre y colores.</p><p className="mt-1 italic">El plan incluye TODOS los módulos y agentes de IA.</p></HelpTip></h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Plan White Label</Label>
                    <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.max_employees} onChange={(e) => setForm({ ...form, max_employees: parseInt(e.target.value) })}>
                      <option value={20}>Hasta 20 empleados - $30,000/año</option>
                      <option value={50}>Hasta 50 empleados - $60,000/año</option>
                      <option value={9999}>Más de 50 empleados - $100,000/año</option>
                    </select>
                  </div>
                  <div><Label>Nombre de la App</Label><Input value={form.wl_app_name} onChange={(e) => setForm({ ...form, wl_app_name: e.target.value })} placeholder="MiRH Pro" data-testid="wl-app-name" /></div>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Globe className="w-3 h-3" />Dominio Personalizado<HelpTip title="DNS / Dominio"><p>El cliente debe crear un registro CNAME en su DNS apuntando a <code>humanoz.click</code></p><p className="mt-1 italic">Ej: rh.miempresa.com CNAME humanoz.click</p></HelpTip></Label>
                  <Input value={form.wl_domain} onChange={(e) => setForm({ ...form, wl_domain: e.target.value })} placeholder="rh.miempresa.com" data-testid="wl-domain" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>URL del Logo</Label><Input value={form.wl_logo_url} onChange={(e) => setForm({ ...form, wl_logo_url: e.target.value })} placeholder="https://..." /></div>
                  <div><Label>Color Primario</Label><div className="flex gap-1 items-center"><input type="color" value={form.wl_primary_color} onChange={(e) => setForm({ ...form, wl_primary_color: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" /><Input value={form.wl_primary_color} onChange={(e) => setForm({ ...form, wl_primary_color: e.target.value })} /></div></div>
                  <div><Label>Color Secundario</Label><div className="flex gap-1 items-center"><input type="color" value={form.wl_secondary_color} onChange={(e) => setForm({ ...form, wl_secondary_color: e.target.value })} className="w-8 h-8 rounded border cursor-pointer" /><Input value={form.wl_secondary_color} onChange={(e) => setForm({ ...form, wl_secondary_color: e.target.value })} /></div></div>
                </div>
              </div>
            )}
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>Anterior</Button>
              <Button onClick={() => setStep(3)} disabled={!form.name}>Siguiente</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 flex items-center gap-1"><User className="w-4 h-4" />Datos del contacto principal de la empresa<HelpTip title="Contacto Principal"><p>Persona dentro de la empresa responsable de la relación comercial y decisiones sobre el sistema.</p><p className="mt-1 italic">Ej: Director de RH, Gerente General, CEO</p></HelpTip></p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nombre Completo *</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="María García López" data-testid="contact-name-input" /></div>
              <div><Label>Puesto / Cargo</Label><Input value={form.contact_position} onChange={(e) => setForm({ ...form, contact_position: e.target.value })} placeholder="Directora de RH" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email de Contacto *</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="maria@empresa.com" data-testid="contact-email-input" /></div>
              <div><Label>Teléfono de Contacto</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="55 9876 5432" /></div>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>Anterior</Button>
              <Button onClick={() => setStep(4)} disabled={!form.contact_name || !form.contact_email}>Siguiente</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Crea el administrador del sistema para esta empresa. Este usuario gestionará la configuración y los empleados.<HelpTip title="Administrador"><p>Este usuario tendrá control total del sistema de la empresa: alta de empleados, nómina, reportes, etc.</p><p className="mt-1 italic">Puedes crear más usuarios después desde el detalle de la empresa.</p></HelpTip></p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nombre del Admin *</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="admin-name-input" placeholder="Juan Pérez" /></div>
              <div><Label>Email del Admin *</Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="admin-email-input" placeholder="admin@empresa.com" /></div>
            </div>
            <div><Label>Contraseña *</Label><Input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="admin-password-input" /></div>

            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <h4 className="font-semibold text-sm">Resumen</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-slate-500">Empresa:</span><span className="font-medium">{form.name}</span>
                <span className="text-slate-500">Industria:</span><span className="font-medium">{form.industry || "-"}</span>
                <span className="text-slate-500">Plan:</span><span className="font-medium">{planType === "whitelabel" ? "White Label" : form.plan}</span>
                <span className="text-slate-500">Contacto:</span><span className="font-medium">{form.contact_name} ({form.contact_position || "Sin cargo"})</span>
                {planType === "whitelabel" && form.wl_domain && (<><span className="text-slate-500">Dominio:</span><span className="font-medium">{form.wl_domain}</span></>)}
                <span className="text-slate-500">Admin:</span><span className="font-medium">{form.admin_email}</span>
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(3)}>Anterior</Button>
              <Button onClick={handleSubmit} disabled={!canSubmit || saving} data-testid="submit-create-company">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : null}
                Crear Empresa
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============ EDIT COMPANY DIALOG ============
const EditCompanyDialog = ({ open, onOpenChange, company, onSave }) => {
  const [form, setForm] = useState({
    name: company.name || "", rfc: company.rfc || "", email: company.email || "",
    phone: company.phone || "", address: company.address || "",
    industry: company.industry || "",
    contact_name: company.contact_name || "", contact_email: company.contact_email || "",
    contact_phone: company.contact_phone || "", contact_position: company.contact_position || "",
    plan: company.plan || "estandar", max_employees: company.max_employees || 10,
    white_label: company.white_label || false,
    wl_app_name: company.wl_app_name || "", wl_domain: company.wl_domain || "",
    wl_logo_url: company.wl_logo_url || "",
    wl_primary_color: company.wl_primary_color || "#10b981", wl_secondary_color: company.wl_secondary_color || "#059669"
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar: {company.name}</DialogTitle>
          <DialogDescription>Modifica los datos de la empresa</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Razón Social *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>RFC</Label><Input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div><Label>Dirección</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div>
            <Label>Giro / Industria</Label>
            <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}>
              <option value="">Seleccionar...</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="border-t pt-4">
            <h3 className="font-semibold text-sm text-slate-700 mb-3 flex items-center gap-2"><User className="w-4 h-4" />Contacto Principal</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nombre</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div><Label>Cargo</Label><Input value={form.contact_position} onChange={(e) => setForm({ ...form, contact_position: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div><Label>Teléfono</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            </div>
          </div>
          <div className="border-t pt-4 grid grid-cols-2 gap-3">
            <div>
              <Label>Plan</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value, white_label: e.target.value === "white_label" })}>
                <option value="estandar">Estándar - $89/emp/mes</option>
                <option value="avanzado">Avanzado - $129/emp/mes</option>
                <option value="pro">Pro - $159/emp/mes</option>
                <option value="white_label">White Label</option>
              </select>
            </div>
            <div><Label>Máx. Empleados</Label><Input type="number" value={form.max_employees} onChange={(e) => setForm({ ...form, max_employees: parseInt(e.target.value) || 10 })} /></div>
          </div>

          {(form.white_label || form.plan === "white_label") && (
            <div className="border-t pt-4 space-y-3">
              <h3 className="font-semibold text-sm text-violet-700 flex items-center gap-2"><Palette className="w-4 h-4" />White Label</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre App</Label><Input value={form.wl_app_name} onChange={(e) => setForm({ ...form, wl_app_name: e.target.value })} /></div>
                <div><Label><Globe className="w-3 h-3 inline mr-1" />Dominio</Label><Input value={form.wl_domain} onChange={(e) => setForm({ ...form, wl_domain: e.target.value })} /></div>
              </div>
              <div><Label>URL Logo</Label><Input value={form.wl_logo_url} onChange={(e) => setForm({ ...form, wl_logo_url: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Color Primario</Label><div className="flex gap-2 items-center"><input type="color" value={form.wl_primary_color} onChange={(e) => setForm({ ...form, wl_primary_color: e.target.value })} className="w-10 h-10 rounded border cursor-pointer" /><Input value={form.wl_primary_color} onChange={(e) => setForm({ ...form, wl_primary_color: e.target.value })} className="flex-1" /></div></div>
                <div><Label>Color Secundario</Label><div className="flex gap-2 items-center"><input type="color" value={form.wl_secondary_color} onChange={(e) => setForm({ ...form, wl_secondary_color: e.target.value })} className="w-10 h-10 rounded border cursor-pointer" /><Input value={form.wl_secondary_color} onChange={(e) => setForm({ ...form, wl_secondary_color: e.target.value })} className="flex-1" /></div></div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => onSave(form)} data-testid="save-edit-company"><Save className="w-4 h-4 mr-1" />Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ SMALL COMPONENTS ============
const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card><CardContent className="p-4 flex items-center gap-3"><div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}><Icon className="w-5 h-5 text-white" /></div><div><p className="text-2xl font-bold">{value}</p><p className="text-xs text-slate-500">{label}</p></div></CardContent></Card>
);

const CompanyTable = ({ companies, onView, onToggle }) => (
  <Table>
    <TableHeader><TableRow><TableHead>Empresa</TableHead><TableHead>Plan</TableHead><TableHead>Dominio</TableHead><TableHead>Usuarios</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
    <TableBody>
      {companies.length === 0 ? (
        <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No hay empresas registradas</TableCell></TableRow>
      ) : companies.map((c) => (
        <TableRow key={c.company_id}>
          <TableCell className="font-medium">{c.name}</TableCell>
          <TableCell><PlanBadge plan={c.plan} /></TableCell>
          <TableCell className="text-sm text-slate-500">{c.wl_domain || "-"}</TableCell>
          <TableCell>{c.user_count || 0}</TableCell>
          <TableCell><StatusBadge status={c.status} /></TableCell>
          <TableCell className="text-right space-x-1">
            <Button size="sm" variant="outline" onClick={() => onView(c)} data-testid={`view-company-${c.company_id}`}><Eye className="w-3 h-3 mr-1" />Ver</Button>
            <Button size="sm" variant={c.status === "active" ? "destructive" : "default"} onClick={() => onToggle(c.company_id)} data-testid={`toggle-company-${c.company_id}`}>
              {c.status === "active" ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const CompanyDetail = ({ company, users, modules, onBack, onToggleCompany, onToggleUser, onImpersonate, onCreateUser, onConfigModules, onEditCompany, onDeleteCompany }) => (
  <div className="space-y-6">
    <div className="flex items-center gap-3 flex-wrap">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-companies"><ChevronLeft className="w-4 h-4 mr-1" />Volver</Button>
      <div className="flex-1 min-w-0">
        <h2 className="text-xl font-bold truncate">{company.name}</h2>
        <p className="text-sm text-slate-500">ID: {company.company_id}</p>
      </div>
      <StatusBadge status={company.status} />
      <Button variant="outline" size="sm" onClick={onEditCompany} data-testid="edit-company-btn"><Pencil className="w-3 h-3 mr-1" />Editar</Button>
      <Button variant={company.status === "active" ? "destructive" : "default"} size="sm" onClick={() => onToggleCompany(company.company_id)}>
        {company.status === "active" ? <><PowerOff className="w-3 h-3 mr-1" />Desactivar</> : <><Power className="w-3 h-3 mr-1" />Activar</>}
      </Button>
      <Button variant="destructive" size="sm" onClick={onDeleteCompany} data-testid="delete-company-btn"><Trash2 className="w-3 h-3 mr-1" />Eliminar</Button>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <InfoCard label="Plan" value={company.plan === "white_label" ? "White Label" : company.plan?.toUpperCase()} />
      <InfoCard label="Máx. Empleados" value={company.max_employees === 9999 ? "Ilimitados" : company.max_employees} />
      <InfoCard label="Empleados Registrados" value={company.employee_count} />
      <InfoCard label="Usuarios del Sistema" value={users.length} />
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <InfoCard label="RFC" value={company.rfc} />
      <InfoCard label="Email" value={company.email} />
      <InfoCard label="Teléfono" value={company.phone} />
      <InfoCard label="Industria" value={company.industry} />
    </div>

    {/* Contact Info */}
    {company.contact_name && (
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" />Contacto Principal<HelpTip title="Contacto Principal"><p>Persona responsable de la relación comercial. Úsalo para seguimiento de ventas y soporte.</p></HelpTip></CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <InfoCard label="Nombre" value={company.contact_name} />
            <InfoCard label="Cargo" value={company.contact_position} />
            <InfoCard label="Email" value={company.contact_email} />
            <InfoCard label="Teléfono" value={company.contact_phone} />
          </div>
        </CardContent>
      </Card>
    )}

    {/* White Label Info */}
    {company.white_label && (
      <Card className="border-violet-200 bg-violet-50/30">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Palette className="w-4 h-4 text-violet-600" />White Label</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <InfoCard label="Nombre App" value={company.wl_app_name || "-"} />
            <InfoCard label="Dominio" value={company.wl_domain || "Sin configurar"} />
            <div>
              <p className="text-xs text-slate-500">Colores</p>
              <div className="flex gap-2 mt-1">
                <div className="w-8 h-8 rounded border" style={{ backgroundColor: company.wl_primary_color || "#10b981" }} title="Primario" />
                <div className="w-8 h-8 rounded border" style={{ backgroundColor: company.wl_secondary_color || "#059669" }} title="Secundario" />
              </div>
            </div>
            {company.wl_logo_url && <div><p className="text-xs text-slate-500">Logo</p><img src={company.wl_logo_url} alt="Logo" className="h-8 mt-1" onError={(e) => { e.target.style.display = "none"; }} /></div>}
          </div>
          {company.wl_domain && (
            <div className="mt-3 p-3 bg-white rounded-lg border text-sm">
              <p className="font-medium text-slate-700 mb-1"><Globe className="w-3 h-3 inline mr-1" />Instrucciones DNS para el cliente:</p>
              <p className="text-slate-500">Crear un registro <strong>CNAME</strong> en su proveedor de DNS:</p>
              <code className="block mt-1 p-2 bg-slate-100 rounded text-xs">{company.wl_domain} CNAME humanoz.click</code>
            </div>
          )}
        </CardContent>
      </Card>
    )}

    {/* Modules */}
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base"><Package className="w-4 h-4 inline mr-2" />Módulos<HelpTip title="Módulos"><p>Selecciona qué módulos tiene acceso esta empresa. Si no seleccionas ninguno, tendrá acceso a todos.</p><p className="mt-1 italic">Ej: Un restaurante puede no necesitar "Reclutamiento" pero sí "Asistencia" y "Nómina"</p></HelpTip></CardTitle>
        <Button size="sm" variant="outline" onClick={onConfigModules} data-testid="config-modules-btn"><Package className="w-3 h-3 mr-1" />Configurar</Button>
      </CardHeader>
      <CardContent>
        {company.modules?.length > 0 ? (
          <div className="flex flex-wrap gap-2">{company.modules.map(m => <Badge key={m} variant="secondary">{modules.find(mod => mod.id === m)?.name || m}</Badge>)}</div>
        ) : <p className="text-slate-500 text-sm">Todos los módulos activos (sin restricción)</p>}
      </CardContent>
    </Card>

    {/* Users */}
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base"><Users className="w-4 h-4 inline mr-2" />Usuarios ({users.length})<HelpTip title="Usuarios del Sistema"><p>Usuarios que acceden al sistema. Cada rol tiene permisos diferentes.</p><p className="mt-1"><strong>Admin:</strong> Control total. <strong>Gerente RH:</strong> Módulos HR. <strong>Supervisor:</strong> Su equipo. <strong>Empleado:</strong> Su perfil.</p></HelpTip></CardTitle>
        <Button size="sm" onClick={onCreateUser} data-testid="add-user-btn"><Plus className="w-3 h-3 mr-1" />Agregar</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Email</TableHead><TableHead>Rol</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.user_id}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><RoleBadge role={u.role} /></TableCell>
                <TableCell><StatusBadge status={u.status} /></TableCell>
                <TableCell className="text-right space-x-1">
                  {["admin_empresa", "gerente_rh"].includes(u.role) && (
                    <Button size="sm" variant="outline" className="text-violet-600 border-violet-200" onClick={() => onImpersonate(u.user_id)} data-testid={`impersonate-${u.user_id}`}><Eye className="w-3 h-3 mr-1" />Acceder como</Button>
                  )}
                  <Button size="sm" variant={u.status === "active" ? "destructive" : "default"} onClick={() => onToggleUser(u.user_id)} data-testid={`toggle-user-${u.user_id}`}>
                    {u.status === "active" ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  </div>
);

const ModuleConfigDialog = ({ open, onOpenChange, modules, companyModules, onSave }) => {
  const [selected, setSelected] = useState(companyModules || []);
  useEffect(() => { setSelected(companyModules || []); }, [companyModules]);
  const toggleModule = (id) => setSelected(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Configurar Módulos</DialogTitle><DialogDescription>Selecciona los módulos activos. Si no seleccionas ninguno, todos estarán disponibles.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {modules.map(m => (
            <label key={m.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected.includes(m.id) ? "bg-emerald-50 border-emerald-300" : "hover:bg-slate-50"}`}>
              <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggleModule(m.id)} className="w-4 h-4 rounded text-emerald-600" />
              <div><p className="font-medium text-sm">{m.name}</p><p className="text-xs text-slate-500">{m.description}</p></div>
            </label>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button onClick={() => onSave(selected)} data-testid="save-modules-btn">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InfoCard = ({ label, value }) => (<Card><CardContent className="p-3"><p className="text-xs text-slate-500">{label}</p><p className="font-semibold text-sm truncate">{value || "-"}</p></CardContent></Card>);
const PlanBadge = ({ plan }) => {
  const s = { enterprise: "bg-emerald-100 text-emerald-700", pro: "bg-blue-100 text-blue-700", basico: "bg-slate-100 text-slate-700", white_label: "bg-violet-100 text-violet-700" };
  return <Badge className={s[plan] || s.basico}>{plan === "white_label" ? "White Label" : plan?.charAt(0).toUpperCase() + plan?.slice(1)}</Badge>;
};
const StatusBadge = ({ status }) => <Badge className={status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{status === "active" ? "Activa" : "Inactiva"}</Badge>;
const RoleBadge = ({ role }) => {
  const l = { admin_empresa: "Admin", gerente_rh: "Gerente RH", supervisor: "Supervisor", empleado: "Empleado" };
  const c = { admin_empresa: "bg-violet-100 text-violet-700", gerente_rh: "bg-blue-100 text-blue-700", supervisor: "bg-amber-100 text-amber-700", empleado: "bg-slate-100 text-slate-700" };
  return <Badge className={c[role] || "bg-slate-100 text-slate-700"}>{l[role] || role}</Badge>;
};

export default SuperAdminPanel;
